import {
  IonButton,
  IonContent,
  IonInput,
  IonLoading,
  IonModal,
  IonRouterLink,
  IonSelect,
  IonSelectOption
} from '@ionic/react';
import React, { useCallback, useRef } from 'react';

import {
  ButtonContainer,
  ModalHeader,
  Padding,
  Spacer
} from '@/components/common';
import { Icon, Text } from '@/components/memo';
import { SearchModal, SelectModal } from '@/components/modals';
import {
  ManualBookingProvider,
  useManualBooking,
  useSubscription
} from '@/contexts';
import { COLUMNS as PATIENT_FIELDS, Patient } from '@/data/Patients';
import { useKeyboardAwareInput, usePaywall } from '@/hooks';
import { Paywall } from '@/pages';
import { GENDER_OPTIONS, SELECT_MODAL_OTHERS } from '@/utils/constants';
import { computeAge } from '@/utils/helpers/computeAge';
import { formatName } from '@/utils/helpers/formatName';
import { getGenderIcon } from '@/utils/helpers/getGenderIcon';
import { now } from '@/utils/helpers/now';
import { parseTime } from '@/utils/helpers/parseTime';
import { handleIonMobileNumberInput } from '@/utils/helpers/sanitizeMobileNumberInput';
import { PAYWALL_SOURCES } from '@/utils/tracker';
import { addMonths, endOfYear, format, isAfter, isEqual } from 'date-fns';
import { arrowBackOutline, searchOutline, warning } from 'ionicons/icons';

import './ManualBooking.css';

interface ManualBookingProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate?: Date | string | null | undefined;
  setSelectedDate?: React.Dispatch<React.SetStateAction<Date>>;
  presentingElement?: HTMLElement | undefined;
}

const ManualBooking: React.FC<ManualBookingProps> = ({
  isOpen,
  onClose,
  selectedDate: _,
  presentingElement
}) => {
  const {
    // Date and Time
    date,
    startTime,
    endTime,
    timeOptions,

    // Purpose
    purpose,
    othersPurpose,
    purposeOptions,
    isPurposeSelected,
    isPurposePreSelected,
    onSelectPurpose,
    isPurposeSelectModalOpen,
    onOpenPurposeSelectModal,
    onClosePurposeSelectModal,

    // Patient Search
    patients,
    isPatientSearchModalOpen,
    onOpenPatientSearchModal,
    onPressPatient,
    onClosePatientSearchModal,

    // Patient Information
    firstName,
    lastName,
    mobileNumber,
    gender,
    birthday,

    // Miscellaneous
    loading,
    errors,
    setErrors,
    onInput,
    submit,
    reset,
    onKeyDown,
    conflictingRequest,
    inputRef
  } = useManualBooking();

  const { isSubscribed, purchasing } = useSubscription();
  const { showPaywall, onOpenPaywall, onClosePaywall } = usePaywall();

  const modal = useRef<HTMLIonModalElement | null>(null);
  const containerRef = useRef<HTMLIonContentElement | null>(null);
  const mobileNumberInputRef = useRef<HTMLIonInputElement | null>(null);

  /**
   * Manages keyboard-aware scrolling behavior for the reason input field in the modal.
   * This custom implementation addresses the limitations of the default hook behavior
   * in this specific context where the modal's bottom space is constrained.
   *
   * @see useKeyboardAwareInput - The base hook that provides keyboard awareness functionality
   * @note This implementation ensures the input field remains visible when the keyboard appears,
   *       preventing it from being obscured by the keyboard in the modal's constrained space.
   */
  useKeyboardAwareInput({
    inputRef: mobileNumberInputRef,
    containerRef
  });

  const copy = {
    subHeader: 'NEW APPOINTMENT',
    header: (
      <>
        Create a new <Text color="primary">appointment</Text>
      </>
    ),
    message: 'Please fill in the details below to create a new appointment.',
    selectPurposeModalTitle: (
      <>
        Select <Text color="primary">procedures</Text>
      </>
    ),
    searchPatientModalTitle: (
      <>
        Search <Text color="primary">patients</Text>
      </>
    ),
    searchPatientModalPlaceholder: 'Search patient name or mobile number',
    othersLabel: 'Others, please specify...',
    othersPlaceholder: 'Enter procedure (optional)',
    datePlaceholder: 'Select a date',
    startTimePlaceholder: 'Select a start time',
    endTimePlaceholder: 'Select an end time',
    purposePlaceholder: 'Example: Braces Adjustment',
    firstNamePlaceholder: "Enter patient's first name",
    lastNamePlaceholder: "Enter patient's last name",
    mobileNumberPlaceholder: "Enter patient's mobile number",
    genderPlaceholder: "Select patient's gender",
    birthdayPlaceholder: "Enter patient's birthday",
    countryCode: '+63',
    genders: {
      male: 'Male',
      female: 'Female',
      other: 'Prefer not to say'
    },
    patientInformation: 'PATIENT INFORMATION'
  };

  const getPatientLabel = (patient: Patient) => {
    const computedAge = computeAge(patient.birthday);
    const patientAge = computedAge > 0 ? `${computedAge} years old` : '';

    return (
      <div className="search-modal-content__patient-label">
        <div className="search-modal-content__patient-name-and-gender">
          <p className="search-modal-content__patient-name">
            {formatName(patient.first_name)}{' '}
            <Text color="primary">{formatName(patient.last_name)}</Text>
          </p>
          {patient.gender && patient.gender !== GENDER_OPTIONS.OTHER && (
            <Icon
              icon={getGenderIcon(patient.gender)}
              className="search-modal-content__patient-gender-icon"
              color="primary"
            />
          )}
        </div>

        <div className="search-modal-content__patient-age">{patientAge}</div>
      </div>
    );
  };

  const onSubmit = useCallback(async () => {
    if (isSubscribed === false) {
      onOpenPaywall({
        source: PAYWALL_SOURCES.MANUAL_BOOKING
      });
      return;
    }
    await submit();
  }, [isSubscribed, onOpenPaywall, submit]);

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => {
        reset();
        onClose();
      }}
      presentingElement={presentingElement}
      ref={modal}
    >
      <IonContent ref={containerRef}>
        <Padding withSafeArea={!presentingElement}>
          <div className="manual-booking">
            <ModalHeader
              leftIcon={arrowBackOutline}
              onClickLeftIcon={() => {
                reset();
                modal.current?.dismiss();
              }}
            />
            <Spacer top={16} bottom={24}>
              <h4 className="manual-booking__sub-header">{copy.subHeader}</h4>
              <h1 className="manual-booking__header">{copy.header}</h1>
              <p className="manual-booking__message">{copy.message}</p>
            </Spacer>

            <Spacer bottom={124}>
              <div className="manual-booking__form">
                <div
                  className={`manual-booking__input ${errors.date ? 'manual-booking__input--error' : ''}`}
                >
                  <IonInput
                    ref={inputRef}
                    label="Date"
                    labelPlacement="stacked"
                    value={date}
                    type="date"
                    placeholder={copy.datePlaceholder}
                    onIonInput={(e: CustomEvent) =>
                      onInput('date', e.detail.value!)
                    }
                    onKeyDown={onKeyDown}
                    min={format(now(), 'yyyy-MM-dd')}
                    max={
                      isAfter(endOfYear(now()), addMonths(now(), 3))
                        ? format(endOfYear(now()), 'yyyy-MM-dd')
                        : format(endOfYear(addMonths(now(), 3)), 'yyyy-MM-dd')
                    }
                  />
                </div>
                <div
                  className={`manual-booking__input ${errors.startTime ? 'manual-booking__input--error' : ''}`}
                >
                  <IonSelect
                    label="Start Time"
                    labelPlacement="stacked"
                    value={startTime}
                    compareWith={(a: string, b: string) =>
                      isEqual(parseTime(a), parseTime(b))
                    }
                    placeholder={copy.startTimePlaceholder}
                    onIonChange={(e: CustomEvent) =>
                      onInput('startTime', e.detail.value!)
                    }
                  >
                    {timeOptions.slice(0, -1).map((time) => (
                      <IonSelectOption key={time} value={time}>
                        {format(parseTime(time), 'h:mm a')}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </div>
                <div
                  className={`manual-booking__input ${errors.endTime ? 'manual-booking__input--error' : ''}`}
                >
                  <IonSelect
                    label="End Time"
                    labelPlacement="stacked"
                    value={endTime}
                    compareWith={(a: string, b: string) =>
                      isEqual(parseTime(a), parseTime(b))
                    }
                    placeholder={copy.endTimePlaceholder}
                    onIonChange={(e: CustomEvent) =>
                      onInput('endTime', e.detail.value!)
                    }
                  >
                    {timeOptions
                      .filter(
                        (time) =>
                          !startTime ||
                          isAfter(parseTime(time), parseTime(startTime))
                      )
                      .map((time) => (
                        <IonSelectOption key={time} value={time}>
                          {format(parseTime(time), 'h:mm a')}
                        </IonSelectOption>
                      ))}
                  </IonSelect>
                </div>
                {conflictingRequest && (
                  <div className="manual-booking__conflict-warning">
                    <Icon icon={warning} />
                    <Text color="warning">
                      <strong>Caution:</strong> The selected timeslot overlaps
                      with an existing appointment. You may continue, but ensure
                      this is intentional.
                    </Text>
                  </div>
                )}
                <div
                  className={`manual-booking__input ${errors.purpose ? 'manual-booking__input--error' : ''}`}
                >
                  <IonInput
                    id="purpose-input"
                    label="Procedures"
                    labelPlacement="stacked"
                    value={purpose}
                    placeholder={copy.purposePlaceholder}
                    onIonInput={(e: CustomEvent) =>
                      onInput('purpose', e.detail.value!)
                    }
                    onKeyDown={onKeyDown}
                    onClick={() => {
                      setErrors({ ...errors, purpose: false });
                      onOpenPurposeSelectModal();
                    }}
                    readonly
                  />
                </div>
                {purpose === SELECT_MODAL_OTHERS && (
                  <div
                    className={`manual-booking__input ${errors.othersPurpose ? 'manual-booking__input--error' : ''}`}
                  >
                    <IonInput
                      id="others-input"
                      label={copy.othersLabel}
                      labelPlacement="stacked"
                      value={othersPurpose}
                      placeholder={copy.othersPlaceholder}
                      onIonInput={(event: CustomEvent) =>
                        onInput('othersPurpose', event.detail.value!)
                      }
                      onKeyDown={onKeyDown}
                    />
                  </div>
                )}
                <div className="manual-booking__form-divider">
                  <p className="manual-booking__form-divider-text">
                    <Text>{copy.patientInformation}</Text>
                  </p>
                  <div className="manual-booking__form-divider-line" />
                </div>
                <div className="manual-booking__search-link-container">
                  <Icon
                    className="manual-booking__search-icon"
                    icon={searchOutline}
                    color="primary"
                    onClick={onOpenPatientSearchModal}
                  />
                  <IonRouterLink
                    className="manual-booking__search-link"
                    onClick={onOpenPatientSearchModal}
                  >
                    Search existing patients
                  </IonRouterLink>
                </div>
                <div
                  className={`manual-booking__input ${errors.firstName ? 'manual-booking__input--error' : ''}`}
                >
                  <IonInput
                    label="First Name"
                    labelPlacement="stacked"
                    value={firstName}
                    placeholder={copy.firstNamePlaceholder}
                    onIonInput={(e: CustomEvent) =>
                      onInput('firstName', e.detail.value!)
                    }
                    onKeyDown={onKeyDown}
                  />
                </div>
                <div
                  className={`manual-booking__input ${errors.lastName ? 'manual-booking__input--error' : ''}`}
                >
                  <IonInput
                    label="Last Name"
                    labelPlacement="stacked"
                    value={lastName}
                    placeholder={copy.lastNamePlaceholder}
                    onIonInput={(e: CustomEvent) =>
                      onInput('lastName', e.detail.value!)
                    }
                    onKeyDown={onKeyDown}
                  />
                </div>
                <div
                  className={`manual-booking__input ${errors.mobileNumber ? 'manual-booking__input--error' : ''}`}
                >
                  <IonInput
                    ref={mobileNumberInputRef}
                    type="tel"
                    label="Mobile Number"
                    labelPlacement="stacked"
                    value={mobileNumber}
                    placeholder={copy.mobileNumberPlaceholder}
                    onIonInput={(e: CustomEvent) =>
                      handleIonMobileNumberInput(e, (sanitized) =>
                        onInput('mobileNumber', sanitized)
                      )
                    }
                    onKeyDown={onKeyDown}
                    maxlength={10}
                  >
                    <div slot="start">{copy.countryCode}</div>
                  </IonInput>
                </div>
                <div
                  className={`manual-booking__input ${errors.gender ? 'manual-booking__input--error' : ''}`}
                >
                  <IonSelect
                    label="Gender (optional)"
                    labelPlacement="stacked"
                    value={gender}
                    placeholder={copy.genderPlaceholder}
                    onIonChange={(e: CustomEvent) =>
                      onInput('gender', e.detail.value!)
                    }
                  >
                    <IonSelectOption value="male">
                      {copy.genders.male}
                    </IonSelectOption>
                    <IonSelectOption value="female">
                      {copy.genders.female}
                    </IonSelectOption>
                    <IonSelectOption value="other">
                      {copy.genders.other}
                    </IonSelectOption>
                  </IonSelect>
                </div>
                <div
                  className={`manual-booking__input ${errors.birthday ? 'manual-booking__input--error' : ''}`}
                >
                  <IonInput
                    label="Birthday (optional)"
                    labelPlacement="stacked"
                    value={birthday}
                    type="date"
                    placeholder={copy.birthdayPlaceholder}
                    onIonInput={(e: CustomEvent) =>
                      onInput('birthday', e.detail.value!)
                    }
                    onKeyDown={onKeyDown}
                  />
                </div>
              </div>
            </Spacer>

            <ButtonContainer>
              <IonButton
                id="create-request-button"
                onClick={onSubmit}
                expand="block"
                fill="solid"
                size="default"
                color="primary"
                strong={true}
              >
                Create Appointment
              </IonButton>
            </ButtonContainer>
          </div>
        </Padding>
        <IonLoading isOpen={loading} message="Loading..." spinner="crescent" />

        <SelectModal
          isOpen={isPurposeSelectModalOpen}
          // TODO: presentingElement is temporarily set to undefined due to an issue with Ionic modal card state when used with IonTabs.
          // Uncomment the line below and remove presentingElement={undefined} once the issue is resolved.
          // presentingElement={modal.current}
          presentingElement={undefined}
          title={copy.selectPurposeModalTitle}
          options={purposeOptions}
          isSelected={isPurposeSelected}
          isPreSelected={isPurposePreSelected}
          onSelect={onSelectPurpose}
          onClose={onClosePurposeSelectModal}
          multiple={true}
        />

        <SearchModal
          isOpen={isPatientSearchModalOpen}
          // TODO: presentingElement is temporarily set to undefined due to an issue with Ionic modal card state when used with IonTabs.
          // Uncomment the line below and remove presentingElement={undefined} once the issue is resolved.
          // presentingElement={modal.current}
          presentingElement={undefined}
          title={copy.searchPatientModalTitle}
          searchPlaceholder={copy.searchPatientModalPlaceholder}
          data={patients}
          getLabel={getPatientLabel}
          onPress={onPressPatient}
          onClose={onClosePatientSearchModal}
          searchFields={[
            PATIENT_FIELDS.FIRST_NAME,
            PATIENT_FIELDS.LAST_NAME,
            PATIENT_FIELDS.MOBILE_NUMBER
          ]}
          sortBy={PATIENT_FIELDS.FIRST_NAME}
        />

        <Paywall
          isOpen={showPaywall}
          onClose={onClosePaywall}
          loading={purchasing}
        />
      </IonContent>
    </IonModal>
  );
};

const ManualBookingWithProvider: React.FC<ManualBookingProps> = (props) => {
  return (
    <ManualBookingProvider {...props}>
      <ManualBooking {...props} />
    </ManualBookingProvider>
  );
};

export default ManualBookingWithProvider;
